import { useState } from 'react';

const CourseInfoSection = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    cargo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Estados para o calendário
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [agendamentoData, setAgendamentoData] = useState({
    nome: '',
    email: '',
    telefone: ''
  });
  const [isSubmittingAgendamento, setIsSubmittingAgendamento] = useState(false);
  const [agendamentoStatus, setAgendamentoStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3qeupgc1ds027kqmvc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nome: '', email: '', telefone: '', cargo: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Funções do calendário
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const isDateDisabled = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateSelect = (day: number) => {
    if (isDateDisabled(day)) return;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(date);
    setSelectedTime('');
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    setSelectedDate(null);
    setSelectedTime('');
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    setSelectedDate(null);
    setSelectedTime('');
  };

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30'
  ];

  const handleAgendamentoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAgendamentoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAgendamentoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      setAgendamentoStatus('error');
      return;
    }

    setIsSubmittingAgendamento(true);
    setAgendamentoStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('nome', agendamentoData.nome);
      formDataToSend.append('email', agendamentoData.email);
      formDataToSend.append('telefone', agendamentoData.telefone);
      formDataToSend.append('data', formatDateDisplay(selectedDate));
      formDataToSend.append('horario', selectedTime);

      const response = await fetch('https://readdy.ai/api/form/d4j09ifb8l4gait66hj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setAgendamentoStatus('success');
        setAgendamentoData({ nome: '', email: '', telefone: '' });
        setSelectedDate(null);
        setSelectedTime('');
      } else {
        setAgendamentoStatus('error');
      }
    } catch (error) {
      setAgendamentoStatus('error');
    } finally {
      setIsSubmittingAgendamento(false);
    }
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: startingDayOfWeek }, (_, i) => i);

  return (
    <section id="inscricao" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Programa Estratégico &amp; Inscrições
            </h2>
            <p className="text-xl text-gray-600">
              Garanta sua vaga agora - turmas limitadas para melhor aproveitamento
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Informações do Curso */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Informações do Curso
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Data</h4>
                    <p className="text-gray-600">"no terceiro domingo de cada mês." ✅
</p>
                    <p className="text-sm text-gray-500">Das 8h30 às 17h00</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Local</h4>
                    <p className="text-gray-600">Aracati Office</p>
                    <a 
                      href="https://share.google/4QEo7XegPOkklWWeS" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 transition-colors underline"
                    >
                      Av. Paulista, 1313 - São Paulo/SP
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-group-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Vagas</h4>
                    <p className="text-gray-600">Limitadas a 30 participantes</p>
                    <p className="text-sm text-orange-600 font-medium">Apenas 8 vagas restantes!</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Investimento</h4>
                    <p className="text-gray-600">
                      <span className="text-2xl font-bold text-green-600">R$ 497</span>
                      <span className="text-gray-500 line-through ml-2">R$ 697</span>
                    </p>
                    <p className="text-sm text-green-600 font-medium">Promoção por tempo limitado</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <i className="ri-time-line text-orange-600 mr-2"></i>
                  <span className="font-semibold text-orange-800">Oferta especial!</span>
                </div>
                <p className="text-orange-700 text-sm">
                  Inscrições até 28/02: desconto de R$ 200 + parcelamento em até 3x sem juros
                </p>
              </div>
            </div>

            {/* Formulário de Inscrição */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Formulário de Inscrição
              </h3>
              
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone/WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-transparent transition-colors"
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div>
                  <label htmlFor="cargo" className="block text-sm font-medium text-gray-700 mb-2">
                    Cargo/Profissão *
                  </label>
                  <select
                    id="cargo"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-transparent transition-colors bg-white text-gray-900 text-base font-medium appearance-none pr-10"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="" className="text-gray-500">Selecione sua profissão</option>
                    <option value="Advogado" className="text-gray-900">Advogado</option>
                    <option value="Bacharel em Direito" className="text-gray-900">Bacharel em Direito</option>
                    <option value="Contador" className="text-gray-900">Contador</option>
                    <option value="Administrador" className="text-gray-900">Administrador</option>
                    <option value="Gestor Público" className="text-gray-900">Gestor Público</option>
                    <option value="Analista Fiscal" className="text-gray-900">Analista Fiscal</option>
                    <option value="Consultor Tributário" className="text-gray-900">Consultor Tributário</option>
                    <option value="Auditor" className="text-gray-900">Auditor</option>
                    <option value="Empresário" className="text-gray-900">Empresário</option>
                    <option value="Outro" className="text-gray-900">Outro</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Processando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Garantir Minha Vaga
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      <span className="text-green-800 font-medium">
                        Inscrição realizada com sucesso! Entraremos em contato em breve.
                      </span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <span className="text-red-800 font-medium">
                        Erro ao enviar inscrição. Tente novamente.
                      </span>
                    </div>
                  </div>
                )}
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Ao se inscrever, você concorda com nossos termos de uso e política de privacidade
                </p>
              </div>
            </div>
          </div>

          {/* Área de Agendamento com Calendário Personalizado */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Selecione uma data e hora
              </h3>
              <p className="text-lg text-gray-600">
                Escolha o melhor dia e horário para sua conversa de 30 minutos
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Calendário */}
                <div className="lg:col-span-1">
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={handlePreviousMonth}
                        className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <i className="ri-arrow-left-s-line text-xl"></i>
                      </button>
                      <h4 className="text-lg font-semibold text-gray-900 capitalize">
                        {formatMonthYear(currentMonth)}
                      </h4>
                      <button
                        onClick={handleNextMonth}
                        className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <i className="ri-arrow-right-s-line text-xl"></i>
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-2 mb-2">
                      {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'].map((day) => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-2">
                      {emptyDays.map((_, index) => (
                        <div key={`empty-${index}`} className="aspect-square"></div>
                      ))}
                      {daysArray.map((day) => {
                        const isDisabled = isDateDisabled(day);
                        const isSelected = selectedDate?.getDate() === day && 
                                         selectedDate?.getMonth() === currentMonth.getMonth() &&
                                         selectedDate?.getFullYear() === currentMonth.getFullYear();
                        
                        return (
                          <button
                            key={day}
                            onClick={() => handleDateSelect(day)}
                            disabled={isDisabled}
                            className={`aspect-square rounded-lg text-sm font-medium transition-all ${
                              isDisabled
                                ? 'text-gray-300 cursor-not-allowed'
                                : isSelected
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-900 hover:bg-blue-50'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedDate && (
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 capitalize">
                        {formatDateDisplay(selectedDate)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Horários */}
                <div className="lg:col-span-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Horários disponíveis
                  </h4>
                  {!selectedDate ? (
                    <div className="text-center py-12 text-gray-500">
                      <i className="ri-calendar-line text-4xl mb-2"></i>
                      <p>Selecione uma data primeiro</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                      {timeSlots.map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          className={`w-full py-3 px-4 rounded-lg text-center font-medium transition-all ${
                            selectedTime === time
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-50 text-gray-900 hover:bg-orange-50 border border-gray-200'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Formulário de Confirmação */}
                <div className="lg:col-span-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Confirme seu agendamento
                  </h4>
                  
                  {selectedDate && selectedTime && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center mb-2">
                        <i className="ri-calendar-check-line text-green-600 mr-2"></i>
                        <span className="font-medium text-green-900 capitalize">
                          {formatDateDisplay(selectedDate)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <i className="ri-time-line text-green-600 mr-2"></i>
                        <span className="font-medium text-green-900">{selectedTime}</span>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleAgendamentoSubmit} data-readdy-form className="space-y-4">
                    <div>
                      <label htmlFor="agendamento-nome" className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        id="agendamento-nome"
                        name="nome"
                        value={agendamentoData.nome}
                        onChange={handleAgendamentoInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                        placeholder="Seu nome completo"
                      />
                    </div>

                    <div>
                      <label htmlFor="agendamento-email" className="block text-sm font-medium text-gray-700 mb-2">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        id="agendamento-email"
                        name="email"
                        value={agendamentoData.email}
                        onChange={handleAgendamentoInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                        placeholder="seu@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="agendamento-telefone" className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        id="agendamento-telefone"
                        name="telefone"
                        value={agendamentoData.telefone}
                        onChange={handleAgendamentoInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                        placeholder="(11) 99999-9999"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmittingAgendamento || !selectedDate || !selectedTime}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap"
                    >
                      {isSubmittingAgendamento ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Processando...
                        </>
                      ) : (
                        <>
                          <i className="ri-check-line mr-2"></i>
                          Confirmar Agendamento
                        </>
                      )}
                    </button>

                    {agendamentoStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center">
                          <i className="ri-check-line text-green-600 mr-2"></i>
                          <span className="text-green-800 font-medium text-sm">
                            Agendamento confirmado! Você receberá um e-mail de confirmação.
                          </span>
                        </div>
                      </div>
                    )}

                    {agendamentoStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                          <i className="ri-error-warning-line text-red-600 mr-2"></i>
                          <span className="text-red-800 font-medium text-sm">
                            Erro ao confirmar agendamento. Tente novamente.
                          </span>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default CourseInfoSection;
